import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Chip,
  Typography,
  IconButton,
  Alert,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAddProjectTag, useRemoveProjectTag } from '../../api/projects';
import { useProject } from '../../api/project';
import { useParams } from 'react-router-dom';

export default function TagsTab() {
  const { projectId } = useParams();
  const { project } = useProject();
  const addTag = useAddProjectTag();
  const removeTag = useRemoveProjectTag();
  const [newTagInput, setNewTagInput] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const handleAddTag = async () => {
    if (!newTagInput.trim() || !projectId) return;
    
    try {
      await addTag.mutateAsync({
        projectId,
        tagName: newTagInput.trim(),
      });
      setNewTagInput('');
      setSnackbar({
        open: true,
        message: 'Tag added successfully',
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Failed to add tag:', error);
      setSnackbar({
        open: true,
        message: error?.message || 'Failed to add tag',
        severity: 'error',
      });
    }
  };

  const handleRemoveTag = async (tagName: string) => {
    if (!projectId) return;
    
    try {
      await removeTag.mutateAsync({
        projectId,
        tagName,
      });
      setSnackbar({
        open: true,
        message: 'Tag removed successfully',
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Failed to remove tag:', error);
      setSnackbar({
        open: true,
        message: error?.message || 'Failed to remove tag',
        severity: 'error',
      });
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddTag();
    }
  };

  return (
    <Box sx={{ backgroundColor: '#1a1d2e', minHeight: '100%', p: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>
        Project Tags
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
        Add tags to organize and filter your projects. Tags are automatically generated based on project name, location, and timezone, but you can add custom tags as well.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter a new tag..."
            value={newTagInput}
            onChange={(e) => setNewTagInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={addTag.isPending}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00d4ad',
                },
              },
              '& .MuiInputBase-input': {
                color: '#fff',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
            }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddTag}
            disabled={!newTagInput.trim() || addTag.isPending}
            sx={{
              backgroundColor: '#00d4ad',
              color: '#000',
              '&:hover': {
                backgroundColor: '#00b894',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
              whiteSpace: 'nowrap',
            }}
          >
            Add Tag
          </Button>
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom sx={{ color: '#fff' }}>
          Current Tags
        </Typography>
        {project?.data?.tags && project.data.tags.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {project.data.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                onDelete={() => handleRemoveTag(tag)}
                deleteIcon={
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveTag(tag)}
                    sx={{ color: 'inherit' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
                sx={{
                  backgroundColor: 'rgba(0, 212, 173, 0.2)',
                  color: '#00d4ad',
                  border: '1px solid rgba(0, 212, 173, 0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 212, 173, 0.3)',
                  },
                  '& .MuiChip-deleteIcon': {
                    color: '#00d4ad',
                  },
                }}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'rgba(255, 255, 255, 0.7)' }}>
            No tags yet. Add your first tag above.
          </Typography>
        )}
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            backgroundColor: snackbar.severity === 'success' ? '#00d4ad' : '#ef5350',
            color: '#000'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

